import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {graphql, ForecastQueryRenderer, ticketingTicketExportUrl} from '../../../graphql/forecastRelay';
import {DashboardToolbar} from '../components/DashboardToolbar';
import {DashboardVisualizationRow} from '../components/DashboardVisualizationRow';
import {DashboardTicketSection} from '../components/DashboardTicketSection';
import {DashboardFilterPanel} from '../components/DashboardFilterPanel';
import {EmptyState} from '../../tickets/components/EmptyState';
import {useDashboardFilters} from '../hooks/useDashboardFilters';
import {useDashboardColumnVisibility} from '../hooks/useDashboardColumnVisibility';
import {useDashboardFilterPanel, type DashboardFilterState} from '../hooks/useDashboardFilterPanel';
import {useDashboardFilterOptions} from '../hooks/useDashboardFilterOptions';
import {useDashboardTickets} from '../hooks/useDashboardTickets';
import {
	DashboardTicketsQuery,
	buildDashboardTicketFilters,
	DASHBOARD_TICKET_PAGE_SIZE,
} from '../hooks/useDashboardTicketsQuery';
import {useDashboardRollups, type DashboardRollupsResult} from '../hooks/useDashboardRollups';
import {buildDashboardAnalyticsInput} from '../utils/buildDashboardAnalyticsInput';
import {buildExportParams, dashboardCsvFilename, triggerExportDownload} from '../utils/downloadDashboardCsv';
import {dashboardTableRows} from '../utils/dashboardTableRows';
import {dashboardTotals} from '../utils/dashboardTotals';
import {isResolvedScope} from '../utils/resolvedScope';
import {DASHBOARD_COLUMNS} from '../constants/dashboardColumns';
import {useTicketPerms} from '../../auth/hooks/useTicketPerms';
import type {DashboardTicket, DashboardTotals} from '../types/dashboardTicket';
import type {DashboardGroupByToken} from '../utils/groupTickets';
import type {DashboardColumnKey} from '../constants/dashboardColumns';
import type {DashboardRouteQuery as DashboardRouteQueryType} from './__generated__/DashboardRouteQuery.graphql';
import type {useDashboardTicketsQueryQuery as DashboardTicketsQueryType} from '../hooks/__generated__/useDashboardTicketsQueryQuery.graphql';
import type {useDashboardTicketsQuery_tickets$key} from '../hooks/__generated__/useDashboardTicketsQuery_tickets.graphql';

const DashboardRouteQueryDoc = graphql`
	query DashboardRouteQuery($input: TicketDashboardAnalyticsInput!) {
		viewer {
			actualPersonId
			ticketDashboardAnalytics(input: $input) {
				gauge {
					achieved
					breached
					total
					ratePct
					targetPct
				}
				trend {
					date
					created
					resolved
					breached
				}
			}
		}
	}
`;

const Container = styled.div`
	padding: ${p => p.theme.sys.spacing[600]} ${p => p.theme.sys.spacing[800]};
`;

const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
	margin-bottom: ${p => p.theme.sys.spacing[500]};
`;

const Title = styled.h1`
	${p => p.theme.sys.font?.heading?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
	margin: 0;
`;

/**
 * A marker, not a button: the PRD asks for a help affordance in the header but
 * names no destination for it, and a focusable control that does nothing on
 * activation is worse than none. Rendering it as a labelled, hoverable mark keeps
 * the affordance without promising an action it can't perform.
 */
const HelpMark = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	border: 1.5px solid ${p => p.theme.sys.color.border.decorative};
	color: ${p => p.theme.sys.color.text.content.auxiliary};
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 600;
	cursor: default;
`;

const ToolbarRow = styled.div`
	margin-bottom: ${p => p.theme.sys.spacing[500]};
`;

const Loading = styled.div`
	padding: 16px;
	color: ${p => p.theme.sys.color.text.content.auxiliary};
`;

const EMPTY_ROWS: readonly DashboardTicket[] = [];

/** The loaded rows, and the query variables they were loaded for. */
interface LoadedTicketRows {
	variables: object | null;
	items: readonly DashboardTicket[];
}

const NO_ROWS_LOADED: LoadedTicketRows = {variables: null, items: EMPTY_ROWS};

const EMPTY_TOTALS: DashboardTotals = {
	total: 0,
	pct_resolved: null,
	sla_achievement_rate: null,
	median_resolution_time_seconds: null,
	median_first_response_time_seconds: null,
};

/**
 * How many filter *groups* are applied, not how many values within them: the badge
 * answers "how much am I filtering by", so ten selected assignees is one
 * narrowing, not ten.
 */
function activeFilterCount(applied: DashboardFilterState): number {
	let count = 0;
	if (applied.assignee.length > 0) count++;
	if (applied.requester.length > 0) count++;
	if (applied.status.length > 0) count++;
	if (applied.priority.length > 0) count++;
	if (applied.channel.length > 0) count++;
	if (applied.tags.length > 0) count++;
	if (applied.slaState.length > 0) count++;
	if (applied.lastActivityRange.from || applied.lastActivityRange.to) count++;
	if (applied.resolvedDateRange.from || applied.resolvedDateRange.to) count++;
	if (applied.firstResponseRange.from || applied.firstResponseRange.to) count++;
	return count;
}

interface DashboardTicketSectionWithQueryProps {
	queryRef: useDashboardTicketsQuery_tickets$key;
	groupBy: DashboardGroupByToken;
	visibleColumns: ReadonlySet<DashboardColumnKey>;
	serverRollups: DashboardRollupsResult | null;
	onItemsChange: (items: DashboardTicket[]) => void;
}

function DashboardTicketSectionWithQuery({
	queryRef,
	groupBy,
	visibleColumns,
	serverRollups,
	onItemsChange,
}: DashboardTicketSectionWithQueryProps) {
	const {items, hasNextPage, isLoadingNext, loadNext} = useDashboardTickets(queryRef);

	// The server rollup covers the whole filtered set, so it is what the row should
	// show. Falling back to the client-side rollup over the loaded page keeps a
	// number in every slot while the rollup is in flight, and for good where it
	// cannot be had at all.
	const totals = useMemo(() => serverRollups?.overall ?? dashboardTotals(items), [serverRollups, items]);
	const groupStats = serverRollups?.groupStats ?? null;

	useEffect(() => {
		onItemsChange(items);
	}, [items, onItemsChange]);

	return (
		<DashboardTicketSection
			tickets={items}
			totals={totals}
			groupBy={groupBy}
			groupStats={groupStats}
			visibleColumns={visibleColumns}
			hasNextPage={hasNextPage}
			isLoadingNext={isLoadingNext}
			onLoadMore={() => loadNext()}
		/>
	);
}

export function DashboardRoute() {
	const {filters, setDateField, setRangePreset, setCustomRange, setGroupBy} = useDashboardFilters();
	const {isOpen, draft, applied, open, close, updateDraft, clearDraft, applyDraft, hasActiveFilters} =
		useDashboardFilterPanel();
	// Narrowing to resolved statuses promotes the resolved-view columns (PRD §8.1);
	// the status filter is the only control that can express that scope.
	const columns = useDashboardColumnVisibility(isResolvedScope(applied.status));
	const filterOptions = useDashboardFilterOptions();
	const {isAdmin} = useTicketPerms();

	// groupBy is intentionally excluded from ticket query variables — changing the
	// grouping re-lays-out loaded rows client-side and must not trigger a refetch.
	const ticketQueryVariables = useMemo(() => {
		const ticketFilters = buildDashboardTicketFilters(applied, filters.from, filters.to, filters.dateField);
		return {first: DASHBOARD_TICKET_PAGE_SIZE, after: null as string | null, filters: ticketFilters ?? null};
	}, [applied, filters.from, filters.to, filters.dateField]);

	// The analytics input mirrors the same filter state so the gauge and trend
	// narrow to the set the reader can recognise from the rows on screen. Panel
	// filters the input cannot express (slaState, panel date ranges) are excluded
	// deliberately in the builder rather than dropped at the wire; see the header
	// comment on `buildDashboardAnalyticsInput` for why.
	const analyticsInput = useMemo(
		() => buildDashboardAnalyticsInput(applied, filters.from, filters.to, filters.dateField),
		[applied, filters.from, filters.to, filters.dateField]
	);

	// The rollup mirrors the ticket query's filter state and adds the grouping, which
	// selects the per-group sub-totals. Changing the grouping therefore re-reads the
	// sub-totals without re-reading a single row.
	const serverRollups = useDashboardRollups(applied, filters.from, filters.to, filters.dateField, filters.groupBy);

	// The loaded ticket rows are tracked at the route level so the toolbar's download
	// button stays in sync without the download logic entering the FQR boundary, and
	// they are tagged with the variables they were loaded for: changing the range or
	// the filters re-keys the query, and until the new rows arrive the previous set is
	// no longer what the reader is looking at. Comparing the tag during render — rather
	// than clearing the rows from an effect — avoids racing the child's own report,
	// which React runs first.
	const [loadedRows, setLoadedRows] = useState<LoadedTicketRows>(NO_ROWS_LOADED);
	const onItemsChange = useCallback(
		(items: DashboardTicket[]) => setLoadedRows({variables: ticketQueryVariables, items}),
		[ticketQueryVariables]
	);
	const ticketItems = loadedRows.variables === ticketQueryVariables ? loadedRows.items : EMPTY_ROWS;
	const tableRows = useMemo(() => dashboardTableRows(ticketItems), [ticketItems]);

	// The export is served by the endpoint, not built from the loaded rows, so it
	// covers every ticket matching the filters rather than the page on screen.
	function handleDownloadCsv() {
		const visibleColumnKeys = DASHBOARD_COLUMNS.filter(col => columns.isVisible(col.key)).map(col => col.key);
		const filename = dashboardCsvFilename({from: filters.from, to: filters.to, dateField: filters.dateField});
		const params = buildExportParams(applied, filters.from, filters.to, filters.dateField, visibleColumnKeys, filename);
		triggerExportDownload(ticketingTicketExportUrl(params), filename);
	}

	// Disabled with nothing loaded (an empty table means an empty export), and for
	// a non-admin: the endpoint is TICKETS_ADMIN-gated and answers a bare 403 with
	// an empty body, so an enabled button would read as a download that does
	// nothing. Gating it is what keeps a permission failure visible as a
	// permission failure.
	const downloadDisabled = tableRows.length === 0 || !isAdmin;

	return (
		<Container>
			<HeaderRow>
				<Title>Dashboard</Title>
				<HelpMark role="img" aria-label="Dashboard help" title="One fixed dashboard per account.">
					?
				</HelpMark>
			</HeaderRow>

			<ToolbarRow>
				<DashboardToolbar
					dateField={filters.dateField}
					onDateFieldChange={setDateField}
					rangePresetId={filters.rangePresetId}
					onRangePresetChange={setRangePreset}
					from={filters.from}
					to={filters.to}
					onCustomRangeChange={setCustomRange}
					groupBy={filters.groupBy}
					onGroupByChange={setGroupBy}
					activeFilterCount={activeFilterCount(applied)}
					filtersActive={hasActiveFilters}
					filterPanelOpen={isOpen}
					onOpenFilterPanel={open}
					onDownloadCsv={handleDownloadCsv}
					downloadDisabled={downloadDisabled}
					isColumnVisible={columns.isVisible}
					onToggleColumn={columns.toggle}
				/>
			</ToolbarRow>

			<ForecastQueryRenderer
				query={DashboardRouteQueryDoc}
				variables={{input: analyticsInput}}
				customLoader={() => <Loading>Loading…</Loading>}
				redirectIfNotLoggedIn
				render={(relayProps: DashboardRouteQueryType['response'] | null) => {
					// `ticketDashboardAnalytics` is nullable — the account may have no ticketing
					// data to aggregate. Say so, rather than leaving the page blank below the
					// heading. (The per-card "no tickets resolved" / "no data" states live in the
					// gauge and chart; this covers having no analytics payload at all.)
					if (!relayProps?.viewer?.ticketDashboardAnalytics) {
						return (
							<EmptyState variant="inset" data-testid="dashboard-no-analytics">
								No ticket analytics available for this period.
							</EmptyState>
						);
					}
					const {gauge, trend} = relayProps.viewer.ticketDashboardAnalytics;
					return <DashboardVisualizationRow gauge={gauge} trend={[...trend]} />;
				}}
			/>

			{/* The ticket query runs independently of the analytics query: their
			    variable shapes and refetch triggers differ, so coupling them to one
			    boundary would entangle range-only changes with full filter changes. */}
			<ForecastQueryRenderer
				query={DashboardTicketsQuery}
				variables={ticketQueryVariables}
				customLoader={() => (
					<DashboardTicketSection
						tickets={[]}
						totals={serverRollups?.overall ?? EMPTY_TOTALS}
						groupBy={filters.groupBy}
						visibleColumns={columns.visible}
						hasNextPage={false}
						isLoadingNext={false}
						onLoadMore={() => {}}
					/>
				)}
				redirectIfNotLoggedIn
				render={(relayProps: DashboardTicketsQueryType['response'] | null) => {
					if (!relayProps?.viewer) {
						return (
							<DashboardTicketSection
								tickets={[]}
								// The rollup is not keyed on the rows, so where it has already
								// answered it can fill the row before a single one has loaded.
								totals={serverRollups?.overall ?? EMPTY_TOTALS}
								groupBy={filters.groupBy}
								visibleColumns={columns.visible}
								hasNextPage={false}
								isLoadingNext={false}
								onLoadMore={() => {}}
							/>
						);
					}
					return (
						<DashboardTicketSectionWithQuery
							queryRef={relayProps.viewer}
							groupBy={filters.groupBy}
							visibleColumns={columns.visible}
							serverRollups={serverRollups}
							onItemsChange={onItemsChange}
						/>
					);
				}}
			/>

			<DashboardFilterPanel
				isOpen={isOpen}
				draft={draft}
				filterOptions={filterOptions}
				onClose={close}
				onApply={applyDraft}
				onUpdateDraft={updateDraft}
				onClearDraft={clearDraft}
			/>
		</Container>
	);
}
